// @flow
import type { FSA } from 'types/redux';
import type { CohortPassenger, CohortDriver } from 'types/entities';
import ga from 'react-ga';

import { API_REQUEST } from 'middlewares/api-middleware';
import { COHORTS, COHORTS_ACTIONS } from 'utils/ga-utils';
import type { Errors } from '../types/errors';

export const COHORT_CREATE: string = 'COHORT_CREATE';
export function cohortCreate(cohortName: string): FSA {
  ga.event({
    category: COHORTS,
    action: COHORTS_ACTIONS.COHORT_CREATE,
  });
  return {
    type: COHORT_CREATE,
    payload: {
      method: 'POST',
      url: '/cohorts/', // Trailing slash is needed.
      data: {
        paxType: 0,
        name: cohortName,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const COHORT_GET: string = 'COHORT_GET';
export function cohortGet(cohortID: string): FSA {
  ga.event({
    category: COHORTS,
    action: COHORTS_ACTIONS.COHORT_GET,
  });
  return {
    type: COHORT_GET,
    payload: {
      method: 'GET',
      url: `/cohorts/${cohortID}`,
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
    },
  };
}

export const COHORT_DELETE: string = 'COHORT_DELETE';
export function cohortDeleteInstant(cohortID: string): FSA {
  return {
    type: COHORT_DELETE,
    payload: {
      cohortID,
    },
  };
}

export function cohortDelete(cohortID: string): FSA {
  ga.event({
    category: COHORTS,
    action: COHORTS_ACTIONS.COHORT_DELETE,
  });
  return {
    type: COHORT_DELETE,
    payload: {
      method: 'DELETE',
      url: `/cohorts/${cohortID}`,
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
    },
  };
}

export const UNSET_COHORT: string = 'UNSET_COHORT';
export function unsetCohort(cohortID: string): FSA {
  ga.event({
    category: COHORTS,
    action: COHORTS_ACTIONS.UNSET_COHORT,
  });
  return {
    type: UNSET_COHORT,
    payload: {
      cohortID,
    },
  };
}

export const COHORT_PASSENGER_ASSOCIATE: string = 'COHORT_PASSENGER_ASSOCIATE';
export function cohortPassengerAssociateInstant(cohortID: string, passengerID: string,
  passenger: CohortPassenger): FSA {
  return {
    type: COHORT_PASSENGER_ASSOCIATE,
    payload: {
      cohortID,
      passengerID,
      passenger,
    },
  };
}

export function cohortPassengerAssociate(cohortID: string,
  passenger: CohortPassenger): FSA {
  ga.event({
    category: COHORTS,
    action: COHORTS_ACTIONS.COHORT_PASSENGER_ASSOCIATE,
  });
  return {
    type: COHORT_PASSENGER_ASSOCIATE,
    payload: {
      method: 'POST',
      url: `/cohorts/${cohortID}/passengers`,
      data: {
        passengerID: passenger.cohortPassengerID,
        apiToken: passenger.apiToken || '',
      },
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      passenger,
    },
  };
}

export const COHORT_PASSENGER_UPDATE: string = 'COHORT_PASSENGER_UPDATE';
export function cohortPassengerUpdate(cohortID: string, passengerID: string,
  passenger: CohortPassenger): FSA {
  ga.event({
    category: COHORTS,
    action: COHORTS_ACTIONS.COHORT_PASSENGER_UPDATE,
  });
  return {
    type: COHORT_PASSENGER_UPDATE,
    payload: {
      cohortID,
      passengerID,
      passenger,
    },
  };
}

export const COHORT_DRIVER_ASSOCIATE: string = 'COHORT_DRIVER_ASSOCIATE';
export function cohortDriverAssociateInstant(cohortID: string, driverID: string,
  driver: CohortDriver): FSA {
  return {
    type: COHORT_DRIVER_ASSOCIATE,
    payload: {
      cohortID,
      driverID,
      driver,
    },
  };
}

export function cohortDriverAssociate(cohortID: string, driver: CohortDriver): FSA {
  ga.event({
    category: COHORTS,
    action: COHORTS_ACTIONS.COHORT_DRIVER_ASSOCIATE,
  });
  return {
    type: COHORT_DRIVER_ASSOCIATE,
    payload: {
      method: 'POST',
      url: `/cohorts/${cohortID}/drivers`,
      data: {
        driverID: driver.cohortDriverID,
        imei: driver.imei,
        deviceID: driver.deviceID,
      },
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      driver,
    },
  };
}

export const COHORT_DRIVER_UPDATE: string = 'COHORT_DRIVER_UPDATE';
export function cohortDriverUpdate(cohortID: string, driverID: string,
  driver: CohortDriver): FSA {
  ga.event({
    category: COHORTS,
    action: COHORTS_ACTIONS.COHORT_DRIVER_UPDATE,
  });
  return {
    type: COHORT_DRIVER_UPDATE,
    payload: {
      cohortID,
      driver,
      driverID,
    },
  };
}

export const GET_COHORTS: string = 'GET_COHORTS';
export function getCohorts(limit: number, lastOffset: ?string, initial: boolean): FSA {
  const lastKey = lastOffset || '';
  return {
    type: GET_COHORTS,
    payload: {
      method: 'GET',
      url: `/users/cohorts?limit=${limit}&lastOffset=${lastKey}`,
    },
    meta: {
      [API_REQUEST]: true,
      initial,
    },
  };
}

export const GET_COHORT_STATUS: string = 'GET_COHORT_STATUS';
export function getCohorStatus(cohortID: string, status: number, error: Errors): FSA {
  return {
    type: GET_COHORT_STATUS,
    payload: {
      cohortID,
      status,
      error,
    },
  };
}

export const DOWNLOAD_COHORT_YAML: string = 'DOWNLOAD_COHORT_YAML';
export function downloadCohortYaml(cohortID: number): FSA {
  return {
    type: DOWNLOAD_COHORT_YAML,
    payload: {
      method: 'GET',
      url: `/cohorts/yaml/${cohortID}`,
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
    },
  };
}



/** WEBPACK FOOTER **
 ** ./src/js/actions/cohorts.js
 **/